import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiUpload } from './client';
import type { ApiInfo, AuditStatus, AuditSummary, LockState } from './types';
import { resetCsrfToken, type CurrentUser } from '../auth';
import type { ArchiveReason, AuditListScope, Dashboard, PagedAudits } from './types';
import { getConnection, resetConnection } from '../signalr';

// Centralised query keys so invalidation is consistent.
export const auditKeys = {
  all:       (tenantId: string) => ['audits', tenantId] as const,
  // Slice 18b — one key per (scope, page) so paging doesn't thrash the
  // cache, nested under `all` so a single invalidate clears every page.
  list:      (tenantId: string, scope: string, skip: number, take: number) =>
    ['audits', tenantId, 'list', scope, skip, take] as const,
  detail:    (tenantId: string, auditId: string) => ['audits', tenantId, auditId] as const,
  lock:      (tenantId: string, auditId: string) => ['audits', tenantId, auditId, 'lock'] as const,
  responses: (tenantId: string, auditId: string) => ['audits', tenantId, auditId, 'responses'] as const,
  scorecard: (tenantId: string, auditId: string) => ['audits', tenantId, auditId, 'scorecard'] as const,
  breakdown: (tenantId: string, auditId: string) => ['audits', tenantId, auditId, 'breakdown'] as const,
  report:    (tenantId: string, auditId: string) => ['audits', tenantId, auditId, 'report'] as const,
  reportVersions:
    (tenantId: string, auditId: string, sectionKey: string) =>
      ['audits', tenantId, auditId, 'report', sectionKey, 'versions'] as const,
  actionPrioritisation:
    (tenantId: string, auditId: string) =>
      ['audits', tenantId, auditId, 'action-prioritisation'] as const,
  activity:
    (tenantId: string, auditId: string) =>
      ['audits', tenantId, auditId, 'activity'] as const,
  evidence:
    (tenantId: string, auditId: string) =>
      ['audits', tenantId, auditId, 'evidence'] as const,
  template:  (versionId: string) => ['audit-template', versionId] as const,
};

export const dpiaKeys = {
  all: (tenantId: string) => ['dpias', tenantId] as const,
};

// Admin / platform-operator surface — global (not tenant-scoped) because
// platform admins work across every tenant.
export const adminKeys = {
  tenants:  ['admin', 'tenants']  as const,
  users:    ['admin', 'users']    as const,
  auditLog: ['admin', 'auditlog'] as const,
};

export type AdminAuditLogEntry = {
  id: string;
  occurredAtUtc: string;
  actorUserId: string;
  actorEmail: string;
  action: string;
  targetType: string;
  targetId: string | null;
  summary: string;
};

export type AdminAuditLogFilter = {
  actorUserId?: string;
  action?: string;
  targetType?: string;
  fromUtc?: string;
  toUtc?: string;
  limit?: number;
};

export function useAdminAuditLog(filter: AdminAuditLogFilter = {}) {
  const qs = new URLSearchParams();
  if (filter.actorUserId) qs.set('actorUserId', filter.actorUserId);
  if (filter.action)      qs.set('action',      filter.action);
  if (filter.targetType)  qs.set('targetType',  filter.targetType);
  if (filter.fromUtc)     qs.set('fromUtc',     filter.fromUtc);
  if (filter.toUtc)       qs.set('toUtc',       filter.toUtc);
  if (filter.limit)       qs.set('limit',       String(filter.limit));
  const suffix = qs.toString();
  return useQuery({
    queryKey: [...adminKeys.auditLog, filter] as const,
    queryFn:  () => api<AdminAuditLogEntry[]>(
      `/api/admin/auditlog${suffix ? `?${suffix}` : ''}`),
    staleTime: 15_000,
  });
}

export type AdminTenant = {
  id: string;
  name: string;
  createdAtUtc: string;
  deletedAtUtc: string | null;
};
export type AdminUser = {
  userId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string;
  tenantId: string;
  tenantName: string;
  isPlatformAdmin: boolean;
  mfaEnabled: boolean;
  isDisabled: boolean;
};

export function useAdminTenants(opts?: { includeDeleted?: boolean }) {
  const includeDeleted = opts?.includeDeleted ?? false;
  return useQuery({
    queryKey: [...adminKeys.tenants, { includeDeleted }] as const,
    queryFn:  () => api<AdminTenant[]>(
      `/api/admin/tenants${includeDeleted ? '?includeDeleted=true' : ''}`),
    staleTime: 60_000,
  });
}

export function useCreateAdminTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string }) =>
      api<AdminTenant>('/api/admin/tenants', { method: 'POST', body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.tenants });
    },
  });
}

export function useUpdateAdminTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; name?: string; delete?: boolean; restore?: boolean }) =>
      api<AdminTenant>(`/api/admin/tenants/${input.id}`, {
        method: 'PATCH',
        body: { name: input.name, delete: input.delete, restore: input.restore },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.tenants });
      // User list joins on tenant name — refetch so renamed/deleted tenants
      // reflect on the users page too.
      void qc.invalidateQueries({ queryKey: adminKeys.users });
    },
  });
}

// Entitlements per tenant. ProductCode strings on the wire match the
// lowercase casing emitted by /api/auth/me ("audittool", "dpia").
export type AdminEntitlement = {
  productCode: string;
  enabledAtUtc: string;
  expiresAtUtc: string | null;
};

export const PRODUCT_LABELS: Record<string, string> = {
  audittool: 'Audit Tool',
  dpia:      'DPIA',
};

export function useAdminEntitlements(tenantId: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'entitlements', tenantId] as const,
    queryFn:  () => api<AdminEntitlement[]>(
      `/api/admin/tenants/${tenantId}/entitlements`),
    enabled: !!tenantId,
    staleTime: 15_000,
  });
}

export function useGrantEntitlement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { tenantId: string; productCode: string }) =>
      api<AdminEntitlement>(
        `/api/admin/tenants/${input.tenantId}/entitlements/${input.productCode}`,
        { method: 'PUT' }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['admin', 'entitlements', vars.tenantId] });
      // Audit log will show the grant — invalidate so it refreshes if open.
      void qc.invalidateQueries({ queryKey: adminKeys.auditLog });
    },
  });
}

export function useRevokeEntitlement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { tenantId: string; productCode: string }) =>
      api<void>(
        `/api/admin/tenants/${input.tenantId}/entitlements/${input.productCode}`,
        { method: 'DELETE' }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['admin', 'entitlements', vars.tenantId] });
      void qc.invalidateQueries({ queryKey: adminKeys.auditLog });
    },
  });
}

export function useAdminUsers() {
  return useQuery({
    queryKey: adminKeys.users,
    queryFn:  () => api<AdminUser[]>('/api/admin/users'),
    staleTime: 30_000,
  });
}

export type CreateAdminUserInput = {
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  tenantId: string;
  isPlatformAdmin: boolean;
};

export type CreateAdminUserResponse = {
  user: AdminUser;
  tempPassword: string;
};

export function useCreateAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAdminUserInput) =>
      api<CreateAdminUserResponse>('/api/admin/users', { method: 'POST', body: input }),
    onSuccess: () => {
      // List is no longer authoritative — refetch so the new user appears.
      void qc.invalidateQueries({ queryKey: adminKeys.users });
    },
  });
}

export type UpdateAdminUserInput = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  tenantId?: string;
  isPlatformAdmin?: boolean;
  isDisabled?: boolean;
};

export function useUpdateAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAdminUserInput) => {
      const { id, ...patch } = input;
      return api<AdminUser>(`/api/admin/users/${id}`,
        { method: 'PATCH', body: patch });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.users });
      // A user's edit might affect their own /me payload if they edited
      // themselves (name change) — refresh defensively.
      void qc.invalidateQueries({ queryKey: authKeys.me });
    },
  });
}

export function useDeleteAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/api/admin/users/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.users });
    },
  });
}

// =============================================================================
// Auth
// =============================================================================

export const authKeys = {
  me: ['auth', 'me'] as const,
};

/** Read the current user from /api/auth/me. Treats 401 as "not logged in"
 *  (returns null) so callers can branch the UI without exception handling. */
export function useCurrentUser() {
  return useQuery({
    queryKey: authKeys.me,
    queryFn: async () => {
      try {
        return await api<CurrentUser>('/api/auth/me');
      } catch (err) {
        if ((err as { status?: number })?.status === 401) return null;
        throw err;
      }
    },
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * Discriminated login result. The server returns either a user (full sign-in
 * completed) or a "requires two-factor" hint. The SPA branches the UI on the
 * discriminator rather than parsing the user shape.
 */
export type LoginResult =
  | { requiresTwoFactor: false; user: CurrentUser }
  | { requiresTwoFactor: true;  user: null };

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (creds: { email: string; password: string; rememberMe?: boolean }) =>
      api<LoginResult>('/api/auth/login', { method: 'POST', body: creds }),
    onSuccess: (result) => {
      if (result.requiresTwoFactor) {
        // No cookie yet — the SPA shows the TOTP step and POSTs to
        // /api/auth/mfa/verify next.
        return;
      }
      qc.setQueryData(authKeys.me, result.user);
      // The auth cookie just changed — drop any cached SignalR connection
      // so the next subscription negotiates with the new identity, and
      // drop the CSRF token, which was minted for the anonymous principal
      // and is rejected by any endpoint that actually validates it.
      resetCsrfToken();
      void resetConnection();
    },
  });
}

/**
 * Submit the TOTP code that completes a two-factor login. Server reads the
 * TwoFactorUserId partial cookie set by the prior password step.
 */
export function useMfaVerify() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      code: string;
      rememberMe?: boolean;
      rememberMachine?: boolean;
    }) => api<CurrentUser>('/api/auth/mfa/verify', { method: 'POST', body: input }),
    onSuccess: (user) => {
      qc.setQueryData(authKeys.me, user);
      resetCsrfToken();
      void resetConnection();
    },
  });
}

/** Submit a recovery code in place of a TOTP code. */
export function useMfaRecover() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { recoveryCode: string; rememberMe?: boolean }) =>
      api<CurrentUser>('/api/auth/mfa/recover', { method: 'POST', body: input }),
    onSuccess: (user) => {
      qc.setQueryData(authKeys.me, user);
      resetCsrfToken();
      void resetConnection();
    },
  });
}

// =============================================================================
// MFA enrollment
// =============================================================================

export type MfaSetupData = {
  sharedKey: string;
  authenticatorUri: string;
};

export type MfaEnableData = {
  user: CurrentUser;
  recoveryCodes: string[];
};

/**
 * Begin MFA enrollment. Issues a fresh authenticator key on every call (any
 * partially-completed previous attempt is invalidated), so the SPA must only
 * trigger this from the dedicated setup page — typically once on mount.
 */
export function useMfaSetup() {
  return useMutation({
    mutationFn: () =>
      api<MfaSetupData>('/api/auth/mfa/setup', { method: 'POST' }),
  });
}

/**
 * Verify the first TOTP code, enable 2FA, and receive the user's recovery
 * codes. The server refreshes the auth cookie so {@code mfaEnabled} flips
 * without a re-login.
 *
 * <b>Deliberate omission:</b> this hook does NOT call
 * {@code qc.setQueryData(authKeys.me, ...)} on success. If it did, App.tsx's
 * gate would see the updated user, flip from MfaSetupPage to the main app,
 * and unmount the setup page before the user has had a chance to read their
 * recovery codes. The caller (MfaSetupPage) promotes the user to "enrolled"
 * after the user confirms they've saved the codes.
 */
export function useMfaEnable() {
  return useMutation({
    mutationFn: (input: { code: string }) =>
      api<MfaEnableData>('/api/auth/mfa/enable', { method: 'POST', body: input }),
    onSuccess: () => {
      // Cookie has been refreshed by RefreshSignInAsync on the server. The
      // SignalR connection negotiated under the old (un-enrolled) cookie
      // must be dropped so the next subscribe goes through the MfaEnrolled-
      // gated hub authorization successfully.
      void resetConnection();
    },
  });
}

/**
 * Slice 15 — change the current user's password. Server verifies the
 * current password, applies the new one, clears MustChangePassword, and
 * refreshes the sign-in cookie so the SPA gate lifts on the next render.
 */
export function useChangePassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      api<{ user: CurrentUser }>('/api/auth/change-password', {
        method: 'POST',
        body:   input,
      }),
    onSuccess: (data) => {
      // Promote the refreshed user in the cache so App.tsx's gate flips
      // to the next step (MFA setup, or the main app if MFA is done).
      // Cookie has been re-emitted server-side; the SignalR connection
      // still holds an old handshake but the change-password page isn't
      // subscribed to anything, so no drop needed here.
      qc.setQueryData(authKeys.me, data.user);
    },
  });
}

/**
 * Generate a new set of 10 recovery codes, invalidating the previous set.
 * Requires the caller's current authenticator code as proof — a stolen
 * session cookie alone shouldn't be enough to rotate codes.
 */
export function useRegenerateRecoveryCodes() {
  return useMutation({
    mutationFn: (input: { code: string }) =>
      api<{ recoveryCodes: string[] }>(
        '/api/auth/mfa/recovery-codes/regenerate',
        { method: 'POST', body: input }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>('/api/auth/logout', { method: 'POST' }),
    onSuccess: () => {
      qc.setQueryData(authKeys.me, null);
      resetCsrfToken();
      void resetConnection();
      // Drop every cached query — they belong to the previous session.
      qc.clear();
    },
  });
}

// =============================================================================
// Misc / audits
// =============================================================================

export function useApiInfo() {
  return useQuery({
    queryKey: ['api-info'],
    queryFn:  () => api<ApiInfo>('/api/info'),
    staleTime: 60_000,
  });
}

/**
 * Slice 18b — the list is now scoped (open / archived / all) and paged. The
 * server clamps `take` to 1..200 and defaults the scope to `open`, so no
 * caller can accidentally pull the whole archive.
 */
/**
 * Cross-module open work. Slice 18c.
 *
 * `refetchOnWindowFocus` is off and `staleTime` is a minute, deliberately:
 * this fans out to one set of aggregate queries per entitled module, and
 * TanStack's default would re-run all of them every time the user alt-tabs
 * back. Harmless on the current flat-rate dev SQL tier, but production is
 * planned as serverless — steady background query traffic is what stops a
 * serverless database ever pausing.
 */
export function useDashboard(enabled = true) {
  return useQuery({
    queryKey: ['dashboard'] as const,
    queryFn:  () => api<Dashboard>('/api/dashboard'),
    enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useAudits(
  tenantId: string | undefined,
  options: { scope?: AuditListScope; skip?: number; take?: number } = {},
) {
  const { scope = 'open', skip = 0, take = 50 } = options;
  const params = new URLSearchParams({
    scope,
    skip: String(skip),
    take: String(take),
  });

  return useQuery({
    queryKey: tenantId
      ? auditKeys.list(tenantId, scope, skip, take)
      : ['audits', 'disabled'],
    queryFn:  () => api<PagedAudits>(`/api/audit/audits?${params}`),
    enabled:  !!tenantId,
    // Slice 16c — audit list rarely changes moment-to-moment; 15s
    // staleTime damps window-focus refetches while still catching a
    // fresh "New audit" in near-realtime for a user actively creating
    // them. Mutations that create/update audits invalidate this key
    // via useCreateAudit's onSuccess, so the staleTime is only a
    // floor on unnecessary refetches, never a source of true staleness.
    staleTime: 15_000,
    // Keeps the previous page on screen while the next one loads, so
    // paging doesn't flash an empty list.
    placeholderData: prev => prev,
  });
}

/**
 * Archive an audit — takes it off the working list. Not a delete: the row
 * stays, the published report stays visible to the client, and unarchiving
 * restores the audit exactly as it was.
 *
 * 409 means either "already archived" or "another auditor holds the lock";
 * the server's message distinguishes them and the caller surfaces it.
 * `force` is platform-admin only and returns 403 otherwise.
 */
export function useArchiveAudit(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      auditId: string;
      reason: ArchiveReason;
      note?: string;
      force?: boolean;
    }) =>
      api<void>(`/api/audit/audits/${input.auditId}/archive`, {
        method: 'POST',
        body:   { reason: input.reason, note: input.note ?? null, force: input.force ?? false },
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: auditKeys.all(tenantId) });
      void qc.invalidateQueries({ queryKey: auditKeys.detail(tenantId, vars.auditId) });
    },
  });
}

/** Put an archived audit back on the working list. */
export function useUnarchiveAudit(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { auditId: string }) =>
      api<void>(`/api/audit/audits/${input.auditId}/archive`, { method: 'DELETE' }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: auditKeys.all(tenantId) });
      void qc.invalidateQueries({ queryKey: auditKeys.detail(tenantId, vars.auditId) });
    },
  });
}

/**
 * Create a new audit. Slice 14+ requires an organisationId — the staff SPA
 * picks a client organisation from a dropdown before submitting, and the
 * server verifies it belongs to the caller's tenant. Passing an empty /
 * unknown org id returns 400 / 404 respectively.
 */
export function useCreateAudit(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { title: string; organisationId: string }) =>
      api<AuditSummary>('/api/audit/audits', {
        method: 'POST',
        body:   { title: input.title, organisationId: input.organisationId },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: auditKeys.all(tenantId) }),
  });
}

// AuditTool: lock — initial state from GET, live updates from SignalR.
export function useLockState(tenantId: string, auditId: string | undefined) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: auditId ? auditKeys.lock(tenantId, auditId) : ['lock', 'disabled'],
    // 204 No Content -> api() returns undefined which TanStack Query v5 rejects.
    // Normalise to null at the query level.
    queryFn: async () => {
      const result = await api<LockState | null>('/api/audit/audits/' + auditId + '/lock');
      return result ?? null;
    },
    enabled:  !!auditId,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!auditId) return;

    let cancelled = false;
    let connection: Awaited<ReturnType<typeof getConnection>> | null = null;

    const handler = (payload: { auditId: string; state: LockState | null }) => {
      if (payload.auditId !== auditId) return;
      qc.setQueryData(auditKeys.lock(tenantId, auditId), payload.state ?? null);
    };

    (async () => {
      try {
        connection = await getConnection();
        if (cancelled) return;
        connection.on('LockChanged', handler);
        await connection.invoke('SubscribeToAudit', auditId);
      } catch (err) {
        console.warn('SignalR subscription failed; falling back to GET-only', err);
      }
    })();

    return () => {
      cancelled = true;
      if (connection) {
        connection.off('LockChanged', handler);
        connection.invoke('UnsubscribeFromAudit', auditId).catch(() => undefined);
      }
    };
  }, [auditId, tenantId, qc]);

  return query;
}

export function useAcquireLock(tenantId: string, auditId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<LockState>('/api/audit/audits/' + auditId + '/lock', { method: 'POST' }),
    onSuccess: (state) => {
      qc.setQueryData(auditKeys.lock(tenantId, auditId), state);
    },
  });
}

export function useHeartbeat(tenantId: string, auditId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<LockState>('/api/audit/audits/' + auditId + '/lock/heartbeat', { method: 'POST' }),
    onSuccess: (state) => qc.setQueryData(auditKeys.lock(tenantId, auditId), state),
  });
}

export function useReleaseLock(tenantId: string, auditId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>('/api/audit/audits/' + auditId + '/lock', { method: 'DELETE' }),
    onSuccess: () => {
      qc.setQueryData(auditKeys.lock(tenantId, auditId), null);
      void qc.invalidateQueries({ queryKey: auditKeys.lock(tenantId, auditId) });
    },
  });
}

// =============================================================================
// DPIA module
// =============================================================================

export type ThresholdAnswers = {
  systematicEvaluationOrProfiling: boolean;
  largeScaleSpecialCategoryData: boolean;
  systematicPublicMonitoring: boolean;
  innovativeTechnologyUse: boolean;
  denialOfServiceOrRight: boolean;
};

export type ThresholdAssessment = {
  id: string;
  outcome: 'DpiaRequired' | 'DpiaNotRequired';
  dpiaId: string | null;
  questionSetVersion: number;
  completedAtUtc: string;
};

export type DpiaSummary = {
  id: string;
  title: string;
  status: 'Draft' | 'InReview' | 'Approved' | 'Archived';
  createdAtUtc: string;
  updatedAtUtc: string;
};

export function useDpias(tenantId: string | undefined) {
  return useQuery({
    queryKey: tenantId ? dpiaKeys.all(tenantId) : ['dpias', 'disabled'],
    queryFn:  () => api<DpiaSummary[]>('/api/dpia/dpias'),
    enabled:  !!tenantId,
    // Slice 16c — same reasoning as useAudits; damps focus refetches.
    staleTime: 15_000,
  });
}

export function useCreateDpia(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { title: string; thresholdAssessmentId?: string | null }) =>
      api<DpiaSummary>('/api/dpia/dpias', {
        method: 'POST',
        body: {
          title: input.title,
          thresholdAssessmentId: input.thresholdAssessmentId ?? null,
        },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: dpiaKeys.all(tenantId) }),
  });
}

export function useSubmitThresholdAssessment() {
  return useMutation({
    mutationFn: (answers: ThresholdAnswers) =>
      api<ThresholdAssessment>('/api/dpia/threshold-assessments', {
        method: 'POST',
        body: answers,
      }),
  });
}

// =============================================================================
// Tenant users (for the assessor dropdown + future mention/assign UX)
// =============================================================================

export type TenantUser = {
  userId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  /** "First Last" if names are set, otherwise email — always safe to render. */
  displayName: string;
};

export function useTenantUsers() {
  return useQuery({
    queryKey: ['auth', 'users'] as const,
    queryFn:  () => api<TenantUser[]>('/api/auth/users'),
    staleTime: 60_000,
  });
}

// =============================================================================
// Audit form — template + per-audit detail + responses (Slice 7b)
// =============================================================================

export type CriterionPriority = 'Low' | 'Medium' | 'High';

export type AuditItem = {
  id: string;
  text: string;
  displayOrder: number;
  // The server still sends `controlledByItemIds`. It is deliberately not
  // typed here: whether an item applies is decided server-side and arrives
  // as `AuditResponses.hiddenItemIds` (Slice 19a).
};

export type AuditCriterion = {
  id: string;
  code: string;
  name: string;
  question: string;
  priority: CriterionPriority;
  displayOrder: number;
  items: AuditItem[];
};

export type AuditSection = {
  id: string;
  code: string;
  name: string;
  displayOrder: number;
  criteria: AuditCriterion[];
};

export type AuditTemplate = {
  versionId: string;
  versionNumber: number;
  versionName: string;
  publishedAtUtc: string;
  sections: AuditSection[];
};

export type AuditDetail = {
  id: string;
  title: string;
  status: AuditStatus;
  templateVersionId: string | null;
  organisationName: string | null;
  assessorName: string | null;
  assessmentDate: string | null;
  createdAtUtc: string;
  /** Last substantive change — see AuditSummary.updatedAtUtc. */
  updatedAtUtc: string;
  dueDate: string | null;
  /** Null while the audit is open. */
  archivedAtUtc: string | null;
  archiveReason: ArchiveReason | null;
  archiveNote: string | null;
};

export type AuditAnswer = {
  id: string;
  criterionItemId: string;
  /** null = unanswered, -1 = N/A, 0..5 = scored. */
  score: number | null;
  /** Per-item auditor's note. null when none. */
  comment: string | null;
  lastUpdatedAtUtc: string;
};

export type AuditComment = {
  id: string;
  criterionId: string;
  text: string;
  lastUpdatedAtUtc: string;
};

export type AuditResponses = {
  answers: AuditAnswer[];
  comments: AuditComment[];
  /**
   * Criterion items the conditional-hide rule currently removes from this
   * audit. Computed server-side by IApplicableItemResolver (Slice 19a) — the
   * SPA must not re-derive it from the template's controller lists.
   */
  hiddenItemIds: string[];
};

/**
 * Static within a session for a given version — every audit on the page uses
 * the same JSON. We key by versionId and let staleTime: Infinity hold it.
 *
 * If versionId is null/undefined (e.g. an audit created before Slice 7a's
 * migration added the column), fall back to the current published template
 * rather than blocking the form indefinitely.
 */
export function useAuditTemplate(versionId: string | null | undefined) {
  const id = versionId ?? null;
  return useQuery({
    queryKey: id ? auditKeys.template(id) : auditKeys.template('__current__'),
    queryFn:  () => api<AuditTemplate>(
      id ? `/api/audit/template?versionId=${id}` : `/api/audit/template`),
    staleTime: Infinity,
  });
}

export function useAuditDetail(tenantId: string, auditId: string | undefined) {
  return useQuery({
    queryKey: auditId ? auditKeys.detail(tenantId, auditId) : ['audit', 'disabled'],
    queryFn:  () => api<AuditDetail>(`/api/audit/audits/${auditId}`),
    enabled:  !!auditId,
    staleTime: 30_000,
  });
}

export function useAuditResponses(tenantId: string, auditId: string | undefined) {
  return useQuery({
    queryKey: auditId ? auditKeys.responses(tenantId, auditId) : ['audit-responses', 'disabled'],
    queryFn:  () => api<AuditResponses>(`/api/audit/audits/${auditId}/responses`),
    enabled:  !!auditId,
    staleTime: 0,    // refetch on focus etc.; small payload
  });
}

/**
 * One evidence attachment. The report coordinates travel with it so the
 * Report tab can group by section without re-walking the template, and
 * `referenceCode` is assigned server-side so the label the auditor sees in
 * the form is the same one the PDF and the client viewer cite.
 */
export type AuditEvidence = {
  id: string;
  criterionItemId: string;
  criterionId: string;
  sectionId: string;
  sectionCode: string;
  criterionCode: string;
  itemText: string;
  referenceCode: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  description: string | null;
  itemComment: string | null;
  /** Pending | Clean | Infected | Skipped. Infected files can't be downloaded. */
  scanStatus: string;
  uploadedAtUtc: string;
  uploadedByUserId: string;
};

export type EvidenceList = { items: AuditEvidence[] };

/**
 * Extensions the server accepts. Mirrored here so an oversized or
 * unsupported file is refused before it goes over the wire — rejecting a
 * 30 MB file after uploading it is a poor trade for the auditor's time.
 * The server re-checks everything; this is convenience, not enforcement.
 */
export const EVIDENCE_ACCEPTED_EXTENSIONS = [
  '.pdf', '.docx', '.xlsx', '.pptx', '.png', '.jpg', '.jpeg',
  '.gif', '.txt', '.csv', '.msg', '.eml',
] as const;

/** Must match AuditEvidenceOptions.MaxFileBytes on the server. */
export const EVIDENCE_MAX_FILE_BYTES = 25 * 1024 * 1024;

export function useAuditEvidence(tenantId: string, auditId: string | undefined) {
  return useQuery({
    queryKey: auditId
      ? auditKeys.evidence(tenantId, auditId)
      : ['audit-evidence', 'disabled'],
    queryFn:  () => api<EvidenceList>(`/api/audit/audits/${auditId}/evidence`),
    enabled:  !!auditId,
    // Attachments change far less often than answers do, and every
    // mutation below invalidates explicitly, so the staleTime only damps
    // window-focus refetches.
    staleTime: 30_000,
  });
}

export function useUploadEvidence(tenantId: string, auditId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      criterionItemId: string;
      file: File;
      onProgress?: (percent: number) => void;
    }) => apiUpload<AuditEvidence>(
      `/api/audit/audits/${auditId}/answers/${input.criterionItemId}/evidence`,
      input.file,
      { onProgress: input.onProgress },
    ),
    onSuccess: () => {
      // No optimistic insert: the server assigns the id, the size it
      // actually stored, and the reference code, and it may refuse the
      // file outright. Showing a row that might vanish is worse than a
      // brief wait.
      void qc.invalidateQueries({ queryKey: auditKeys.evidence(tenantId, auditId) });
      // The report renders evidence per section and in the register.
      void qc.invalidateQueries({ queryKey: auditKeys.report(tenantId, auditId) });
    },
  });
}

export function useUpdateEvidenceDescription(tenantId: string, auditId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { evidenceId: string; description: string | null }) =>
      api<void>(`/api/audit/audits/${auditId}/evidence/${input.evidenceId}`,
        { method: 'PATCH', body: { description: input.description } }),
    onMutate: async (input) => {
      const key = auditKeys.evidence(tenantId, auditId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<EvidenceList>(key);
      if (previous) {
        qc.setQueryData<EvidenceList>(key, {
          items: previous.items.map(e =>
            e.id === input.evidenceId ? { ...e, description: input.description } : e),
        });
      }
      return { previous };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(auditKeys.evidence(tenantId, auditId), ctx.previous);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: auditKeys.report(tenantId, auditId) });
    },
  });
}

export function useDeleteEvidence(tenantId: string, auditId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (evidenceId: string) =>
      api<void>(`/api/audit/audits/${auditId}/evidence/${evidenceId}`,
        { method: 'DELETE' }),
    onMutate: async (evidenceId) => {
      const key = auditKeys.evidence(tenantId, auditId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<EvidenceList>(key);
      if (previous) {
        qc.setQueryData<EvidenceList>(key, {
          items: previous.items.filter(e => e.id !== evidenceId),
        });
      }
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(auditKeys.evidence(tenantId, auditId), ctx.previous);
      }
    },
    onSettled: () => {
      // Reference codes renumber within a criterion when one is removed,
      // so the whole list has to come back rather than just dropping a row.
      void qc.invalidateQueries({ queryKey: auditKeys.evidence(tenantId, auditId) });
      void qc.invalidateQueries({ queryKey: auditKeys.report(tenantId, auditId) });
    },
  });
}

/** Staff download URL for one attachment. */
export function evidenceDownloadUrl(auditId: string, evidenceId: string): string {
  return `/api/audit/audits/${auditId}/evidence/${evidenceId}/content`;
}

/** Human file size, matching the PDF renderer's FormatSize. */
export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

export type UpdateAuditMetadataInput = {
  organisationName?: string | null;
  assessorName?: string | null;
  assessmentDate?: string | null;
  status?: string | null;
};

export function useUpdateAuditMetadata(tenantId: string, auditId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: UpdateAuditMetadataInput) =>
      api<AuditDetail>(`/api/audit/audits/${auditId}`, { method: 'PATCH', body: patch }),
    onSuccess: (audit) => {
      qc.setQueryData(auditKeys.detail(tenantId, auditId), audit);
      // The list view's row needs to reflect any title/status change too.
      void qc.invalidateQueries({ queryKey: auditKeys.all(tenantId) });
    },
  });
}

/**
 * Optimistic upsert of a per-item answer. Score and comment travel together
 * — the server stores both on one row keyed by (auditId, criterionItemId).
 * The mutation accepts a partial input ({score?} or {comment?}) and merges
 * with the cached row so changing one field doesn't blow away the other.
 */
export function useUpsertAnswer(tenantId: string, auditId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      criterionItemId: string;
      score?: number | null;
      comment?: string | null;
    }) => {
      // Read the current cached row so we can send both fields. If we sent
      // only the field that changed, the server's PUT would null out the
      // other one (whichever field the JSON omits deserialises as null).
      const key = auditKeys.responses(tenantId, auditId);
      const cached = qc.getQueryData<AuditResponses>(key);
      const existing = cached?.answers.find(a => a.criterionItemId === input.criterionItemId);
      const score   = 'score'   in input ? input.score   : existing?.score   ?? null;
      const comment = 'comment' in input ? input.comment : existing?.comment ?? null;
      return api<AuditAnswer>(
        `/api/audit/audits/${auditId}/answers/${input.criterionItemId}`,
        { method: 'PUT', body: { score, comment } },
      );
    },
    onMutate: async (input) => {
      const key = auditKeys.responses(tenantId, auditId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<AuditResponses>(key);
      if (previous) {
        const idx = previous.answers.findIndex(a => a.criterionItemId === input.criterionItemId);
        const base: AuditAnswer = idx === -1
          ? {
              id: 'optimistic-' + input.criterionItemId,
              criterionItemId: input.criterionItemId,
              score: null,
              comment: null,
              lastUpdatedAtUtc: new Date().toISOString(),
            }
          : previous.answers[idx];

        const stamped: AuditAnswer = {
          ...base,
          score:   'score'   in input ? (input.score   ?? null) : base.score,
          comment: 'comment' in input ? (input.comment ?? null) : base.comment,
          lastUpdatedAtUtc: new Date().toISOString(),
        };
        const nextAnswers = idx === -1
          ? [...previous.answers, stamped]
          : previous.answers.map((a, i) => i === idx ? stamped : a);
        qc.setQueryData<AuditResponses>(key, { ...previous, answers: nextAnswers });
      }
      return { previous };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(auditKeys.responses(tenantId, auditId), ctx.previous);
      }
    },
    onSuccess: (saved, input) => {
      const key = auditKeys.responses(tenantId, auditId);
      const current = qc.getQueryData<AuditResponses>(key);
      if (current) {
        // Replace the optimistic row with the server's authoritative copy
        // (keeps the real id + lastUpdatedAtUtc).
        const answers = current.answers.map(a =>
          a.criterionItemId === saved.criterionItemId ? saved : a);
        qc.setQueryData<AuditResponses>(key, { ...current, answers });
      }
      // Slice 19a: which items are hidden is decided server-side and
      // arrives as `hiddenItemIds` on /responses. Any score change can hide
      // or reveal dependent items, so refetch whenever the score was part of
      // this save. Comment-only saves can't change visibility. Cheap (small
      // payload) and keeps the controller→controlled graph out of JS.
      if ('score' in input) {
        void qc.invalidateQueries({ queryKey: auditKeys.responses(tenantId, auditId) });
      }
      // Saving a hiding score (0 = Nothing in Place, -1 = N/A) also runs a
      // server-side cascade that hard-clears answers on dependent items
      // (picked up by the refetch above) and, since Slice 17, soft-deletes
      // their evidence — so the attachment list is stale too.
      if (saved.score === 0 || saved.score === -1) {
        void qc.invalidateQueries({ queryKey: auditKeys.evidence(tenantId, auditId) });
      }
      // Slice 7c: results depend on answers — invalidate so the next visit
      // to the Results tab picks up the new totals.
      void qc.invalidateQueries({ queryKey: auditKeys.scorecard(tenantId, auditId) });
      void qc.invalidateQueries({ queryKey: auditKeys.breakdown(tenantId, auditId) });
      // Slice 9: action prioritisation derives directly from current
      // answers — invalidate so the Report tab's auto-updating table picks
      // up the new row (or drops one that's now Complete).
      void qc.invalidateQueries({ queryKey: auditKeys.actionPrioritisation(tenantId, auditId) });
    },
  });
}

export function useUpsertComment(tenantId: string, auditId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { criterionId: string; text: string }) =>
      api<AuditComment | undefined>(
        `/api/audit/audits/${auditId}/comments/${input.criterionId}`,
        { method: 'PUT', body: { text: input.text } },
      ),
    onSuccess: (saved, input) => {
      const key = auditKeys.responses(tenantId, auditId);
      const current = qc.getQueryData<AuditResponses>(key);
      if (!current) return;
      // Empty text deletes server-side; the response is 204 -> undefined.
      // Otherwise replace or add the comment row.
      let comments = current.comments.filter(c => c.criterionId !== input.criterionId);
      if (saved) {
        comments = [...comments, saved];
      }
      qc.setQueryData<AuditResponses>(key, { ...current, comments });
    },
  });
}

// =============================================================================
// Audit scorecard + breakdown (Slice 7c)
// =============================================================================

export type ComplianceBand =
  | 'NotApplicable'
  | 'VeryLow'
  | 'Low'
  | 'Medium'
  | 'High'
  | 'VeryHigh';

export type OverallScore = {
  totalScore: number;
  potentialScore: number;
  /** 0..1 */
  percentage: number;
  band: ComplianceBand;
};

export type SectionScore = {
  sectionId: string;
  code: string;
  name: string;
  displayOrder: number;
  totalScore: number;
  potentialScore: number;
  percentage: number;
  band: ComplianceBand;
};

export type Scorecard = {
  auditId: string;
  overall: OverallScore;
  sections: SectionScore[];
};

export type StatusCounts = {
  complete: number;
  workRequired: number;
  majorWork: number;
  nothingInPlace: number;
  unanswered: number;
};

export type PriorityStatusGrid = {
  high: StatusCounts;
  medium: StatusCounts;
  low: StatusCounts;
  total: StatusCounts;
};

export type SectionBreakdown = {
  sectionId: string;
  code: string;
  name: string;
  displayOrder: number;
  counts: PriorityStatusGrid;
};

export type Breakdown = {
  auditId: string;
  overall: PriorityStatusGrid;
  sections: SectionBreakdown[];
};

/**
 * Scorecard and breakdown are cheap (small payloads, computed on demand) and
 * change every time an answer is edited. We invalidate both whenever an
 * answer mutation lands, but keep them around with a short stale window so
 * the user can flip between Form and Results without an extra fetch.
 */
export function useScorecard(tenantId: string, auditId: string | undefined) {
  return useQuery({
    queryKey: auditId ? auditKeys.scorecard(tenantId, auditId) : ['scorecard', 'disabled'],
    queryFn:  () => api<Scorecard>(`/api/audit/audits/${auditId}/scorecard`),
    enabled:  !!auditId,
    staleTime: 5_000,
  });
}

export function useBreakdown(tenantId: string, auditId: string | undefined) {
  return useQuery({
    queryKey: auditId ? auditKeys.breakdown(tenantId, auditId) : ['breakdown', 'disabled'],
    queryFn:  () => api<Breakdown>(`/api/audit/audits/${auditId}/breakdown`),
    enabled:  !!auditId,
    staleTime: 5_000,
  });
}

// Extract conflicting lock state from a 409 error. Duck-typed (no instanceof)
// so the check survives Vite HMR module duplication and any future
// error-wrapping by TanStack Query.
export function lockConflictHolder(err: unknown): LockState | null {
  if (!err || typeof err !== 'object') return null;
  const e = err as { status?: unknown; body?: unknown };
  if (e.status !== 409) return null;
  if (!e.body || typeof e.body !== 'object') return null;

  const body = e.body as Partial<LockState>;
  if (typeof body.auditId !== 'string' || typeof body.lockedByUserId !== 'string') return null;
  return body as LockState;
}

// =============================================================================
// Audit report (Slice 9) — section catalog, narrative editing with version
// history, computed sections rendered live from the form.
// =============================================================================

export type ReportSectionKind = 'Narrative' | 'Computed';

/**
 * Lowercase enum names emitted by the server (e.g. "cover", "scorecard_table").
 * Used by the UI to pick which computed renderer to mount.
 */
export type ComputedKind =
  | 'cover'
  | 'tableofcontents'
  | 'scopeareas'
  | 'scorecardtable'
  | 'heatmap'
  | 'actionprioritisation'
  | 'actiontimeline'
  | 'evidenceregister';

export type ReportSection = {
  id: string;
  key: string;
  title: string;
  displayOrder: number;
  kind: ReportSectionKind;
  computedKind: ComputedKind | null;
  currentRevision: number | null;
  /** Tiptap ProseMirror JSON document (stringified). Null when never saved. */
  contentJson: string | null;
  lastUpdatedAtUtc: string | null;
  lastUpdatedByUserId: string | null;
};

/** How the report draws compliance by section. */
export type ScoreChartStyle = 'Radar' | 'Bar' | 'Stacked' | 'Circle';

export type AuditReport = {
  auditId: string;
  sections: ReportSection[];
  /** The chart the report (and the next saved version) uses for compliance by section. */
  scoreChart: ScoreChartStyle;
  /**
   * The audit's live evidence in canonical order. Carried alongside the
   * sections (not inside them) because an attachment is cited twice — once
   * under its own section, once in the register — and duplicating it would
   * let the two drift.
   */
  evidence: AuditEvidence[];
};

export type ReportSectionVersion = {
  revision: number;
  createdAtUtc: string;
  createdByUserId: string;
};

export type ReportSectionVersionContent = ReportSectionVersion & {
  contentJson: string;
};

export type ActionPrioritisationRow = {
  refNumber: number;
  sectionId: string;
  sectionCode: string;
  sectionName: string;
  criterionId: string;
  criterionCode: string;
  criterionName: string;
  criterionItemId: string;
  itemText: string;
  priority: 'Low' | 'Medium' | 'High';
  recommendedAction: string | null;
  currentScore: number | null;
};

export type ActionPrioritisation = {
  auditId: string;
  rows: ActionPrioritisationRow[];
};

export function useAuditReport(tenantId: string, auditId: string | undefined) {
  return useQuery({
    queryKey: auditId ? auditKeys.report(tenantId, auditId) : ['audits', tenantId, 'report-pending'],
    queryFn:  () => api<AuditReport>(`/api/audit/audits/${auditId}/report`),
    enabled:  !!auditId,
    // Slice 16c — was 0, which meant every window focus / component
    // remount re-hit /report (an expensive endpoint that composes
    // sections + fetches the latest version per section). SignalR's
    // useAuditReportRealtime already invalidates this key when a
    // section saves, so a 30s floor is safe.
    staleTime: 30_000,
  });
}

export function useReportSectionVersions(
  tenantId: string,
  auditId: string | undefined,
  sectionKey: string | undefined,
) {
  return useQuery({
    queryKey: auditId && sectionKey
      ? auditKeys.reportVersions(tenantId, auditId, sectionKey)
      : ['audits', tenantId, 'report-versions-pending'],
    queryFn:  () => api<ReportSectionVersion[]>(
      `/api/audit/audits/${auditId}/report/sections/${sectionKey}/versions`),
    enabled:  !!auditId && !!sectionKey,
    staleTime: 30_000,
  });
}

/**
 * Mutation to append a new revision for one narrative section. On success,
 * the report query is patched in-place (new currentRevision + contentJson)
 * to avoid a refetch round-trip; the versions list is invalidated so the
 * dropdown reflects the new revision the next time it's opened.
 */
/**
 * Choose the report's compliance-by-section chart. Optimistic: the Report
 * tab switches straight away and rolls back if the server refuses.
 */
export function useSetScoreChart(tenantId: string, auditId: string) {
  const qc = useQueryClient();
  const key = auditKeys.report(tenantId, auditId);
  return useMutation({
    mutationFn: (scoreChart: ScoreChartStyle) =>
      api<void>(`/api/audit/audits/${auditId}/report/score-chart`,
        { method: 'PUT', body: { scoreChart } }),
    onMutate: async scoreChart => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<AuditReport>(key);
      if (previous) qc.setQueryData<AuditReport>(key, { ...previous, scoreChart });
      return { previous };
    },
    onError: (_err, _style, context) => {
      if (context?.previous) qc.setQueryData(key, context.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: auditKeys.activity(tenantId, auditId) });
    },
  });
}

export function useSaveReportSection(tenantId: string, auditId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { sectionKey: string; contentJson: string }) =>
      api<{ revision: number; createdAtUtc: string; contentJson: string }>(
        `/api/audit/audits/${auditId}/report/sections/${input.sectionKey}`,
        { method: 'PUT', body: { contentJson: input.contentJson } },
      ),
    onSuccess: (saved, input) => {
      // The server stores its canonical form, after reconciling form-note
      // markers (Slices 19b/19c), so cache what it stored, not what we sent.
      // A save can also resolve form-note insertions: refresh the drawer.
      void qc.invalidateQueries({ queryKey: ['audits', tenantId, auditId, 'form-notes'] });
      const key = auditKeys.report(tenantId, auditId);
      const current = qc.getQueryData<AuditReport>(key);
      if (current) {
        qc.setQueryData<AuditReport>(key, {
          ...current,
          sections: current.sections.map(s =>
            s.key === input.sectionKey
              ? { ...s,
                  currentRevision:  saved.revision,
                  contentJson:      saved.contentJson,
                  lastUpdatedAtUtc: saved.createdAtUtc }
              : s),
        });
      }
      void qc.invalidateQueries({
        queryKey: auditKeys.reportVersions(tenantId, auditId, input.sectionKey),
      });
    },
  });
}

/**
 * Fetch a specific revision's content — used when the user picks an older
 * revision from the version dropdown to load it into the editor.
 */
export function getReportSectionVersionContent(
  auditId: string, sectionKey: string, revision: number,
): Promise<ReportSectionVersionContent> {
  return api<ReportSectionVersionContent>(
    `/api/audit/audits/${auditId}/report/sections/${sectionKey}/versions/${revision}`);
}

// Report-level snapshots (Slice 9b)

export type ReportSnapshot = {
  id: string;
  label: string | null;
  createdAtUtc: string;
  createdByUserId: string;
  sectionCount: number;
  // Slice 13 publish lifecycle. Both null → draft; publishedAtUtc set +
  // unpublishedAtUtc null → currently published to client; both set →
  // retracted.
  publishedAtUtc: string | null;
  publishedByUserId: string | null;
  unpublishedAtUtc: string | null;
  unpublishedByUserId: string | null;
};

/** Derived UI state from the four publish-lifecycle fields. */
export type SnapshotState = 'draft' | 'published' | 'retracted';

export function snapshotState(s: ReportSnapshot): SnapshotState {
  if (s.publishedAtUtc && !s.unpublishedAtUtc) return 'published';
  if (s.publishedAtUtc && s.unpublishedAtUtc)  return 'retracted';
  return 'draft';
}

export type ReportSnapshotSection = {
  id: string;
  key: string;
  title: string;
  displayOrder: number;
  kind: ReportSectionKind;
  computedKind: ComputedKind | null;
  revision: number | null;
  contentJson: string | null;
};

export type ReportSnapshotContent = {
  auditId: string;
  snapshotId: string;
  label: string | null;
  createdAtUtc: string;
  sections: ReportSnapshotSection[];
  /** The evidence set frozen when this version was captured. */
  evidence: AuditEvidence[];
  /** The chart style frozen when this version was captured. */
  scoreChart: ScoreChartStyle;
};

const reportSnapshotKeys = {
  list: (tenantId: string, auditId: string) =>
    ['audits', tenantId, auditId, 'report-snapshots'] as const,
};

export function useReportSnapshots(tenantId: string, auditId: string | undefined) {
  return useQuery({
    queryKey: auditId
      ? reportSnapshotKeys.list(tenantId, auditId)
      : ['audits', tenantId, 'report-snapshots-pending'],
    queryFn:  () => api<ReportSnapshot[]>(`/api/audit/audits/${auditId}/report/snapshots`),
    enabled:  !!auditId,
    staleTime: 30_000,
  });
}

export function useCreateReportSnapshot(tenantId: string, auditId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { label?: string | null }) =>
      api<ReportSnapshot>(`/api/audit/audits/${auditId}/report/snapshots`,
        { method: 'POST', body: { label: input.label ?? null } }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: reportSnapshotKeys.list(tenantId, auditId),
      });
    },
  });
}

/**
 * Publish a snapshot to the audit's client organisation (Slice 13).
 * Republishing a retracted snapshot uses this same endpoint — the server
 * clears the Unpublished pair and stamps a fresh PublishedAtUtc.
 */
export function usePublishReportSnapshot(tenantId: string, auditId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (snapshotId: string) =>
      api<void>(
        `/api/audit/audits/${auditId}/report/snapshots/${snapshotId}/publish`,
        { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: reportSnapshotKeys.list(tenantId, auditId),
      });
      void qc.invalidateQueries({ queryKey: auditKeys.activity(tenantId, auditId) });
    },
  });
}

/** Retract a currently-published snapshot (Slice 13). */
export function useUnpublishReportSnapshot(tenantId: string, auditId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (snapshotId: string) =>
      api<void>(
        `/api/audit/audits/${auditId}/report/snapshots/${snapshotId}/unpublish`,
        { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: reportSnapshotKeys.list(tenantId, auditId),
      });
      void qc.invalidateQueries({ queryKey: auditKeys.activity(tenantId, auditId) });
    },
  });
}

/**
 * Imperative fetch (not a hook) — the preview modal triggers this when
 * the user picks a specific snapshot, which is a discrete event rather
 * than a reactive subscription.
 */
export function getReportSnapshotContent(
  auditId: string, snapshotId: string,
): Promise<ReportSnapshotContent> {
  return api<ReportSnapshotContent>(
    `/api/audit/audits/${auditId}/report/snapshots/${snapshotId}`);
}

export function useActionPrioritisation(tenantId: string, auditId: string | undefined) {
  return useQuery({
    queryKey: auditId
      ? auditKeys.actionPrioritisation(tenantId, auditId)
      : ['audits', tenantId, 'action-prioritisation-pending'],
    queryFn:  () => api<ActionPrioritisation>(
      `/api/audit/audits/${auditId}/report/action-prioritisation`),
    enabled:  !!auditId,
    // Slice 16c — was 0. Prioritisation derives from live answers, so
    // it does change with edits, but the SPA already invalidates this
    // key on save. 30s floor prevents redundant recomputes on window
    // focus / remount without introducing user-visible lag.
    staleTime: 30_000,
  });
}

/**
 * Live-update subscription for the report builder. Joins the report
 * SignalR group for this audit on mount, invalidates the composed report
 * (+ affected section's version list) when a co-auditor saves a section,
 * and invalidates the snapshot list when someone captures a new version.
 *
 * Uses a dedicated report group on the hub (see AuditToolHub.
 * ReportGroupName) so unmounting this hook can't kick a sibling lock
 * subscription out of its own group.
 *
 * Best-effort by design: hub failures are logged and swallowed; the
 * REST endpoints keep working (falls back to stale-timer refetches).
 */
export function useAuditReportRealtime(
  tenantId: string,
  auditId: string | undefined,
) {
  const qc = useQueryClient();

  useEffect(() => {
    if (!auditId) return;

    let cancelled = false;
    let connection: Awaited<ReturnType<typeof getConnection>> | null = null;

    const onSectionSaved = (payload: {
      auditId: string;
      sectionKey: string;
      revision: number;
    }) => {
      if (payload.auditId !== auditId) return;
      // Refetch the composed report so the sidebar band + editor pick up
      // the new content; version list too so the dropdown reflects the
      // new revision without the user having to reopen it; activity
      // feed so the right-rail shows the new entry.
      void qc.invalidateQueries({ queryKey: auditKeys.report(tenantId, auditId) });
      void qc.invalidateQueries({
        queryKey: auditKeys.reportVersions(tenantId, auditId, payload.sectionKey),
      });
      void qc.invalidateQueries({ queryKey: auditKeys.activity(tenantId, auditId) });
    };

    const onSnapshotCreated = (payload: {
      auditId: string;
      snapshotId: string;
      label: string | null;
    }) => {
      if (payload.auditId !== auditId) return;
      void qc.invalidateQueries({
        queryKey: reportSnapshotKeys.list(tenantId, auditId),
      });
      void qc.invalidateQueries({ queryKey: auditKeys.activity(tenantId, auditId) });
    };

    (async () => {
      try {
        connection = await getConnection();
        if (cancelled) return;
        connection.on('ReportSectionSaved', onSectionSaved);
        connection.on('ReportSnapshotCreated', onSnapshotCreated);
        await connection.invoke('SubscribeToAuditReport', auditId);
      } catch (err) {
        console.warn(
          'SignalR report subscription failed; falling back to stale-timer refetches',
          err,
        );
      }
    })();

    return () => {
      cancelled = true;
      if (connection) {
        connection.off('ReportSectionSaved', onSectionSaved);
        connection.off('ReportSnapshotCreated', onSnapshotCreated);
        connection
          .invoke('UnsubscribeFromAuditReport', auditId)
          .catch(() => undefined);
      }
    };
  }, [auditId, tenantId, qc]);
}

// -----------------------------------------------------------------------
//  Slice 12 — audit activity feed
// -----------------------------------------------------------------------

export type AuditActivityKind =
  | 'AuditCreated'
  | 'ReportSectionSaved'
  | 'ReportSnapshotCreated'
  // Slice 13 publish lifecycle
  | 'ReportPublished'
  | 'ReportUnpublished';

export type AuditActivityItem = {
  id: string;
  kind: AuditActivityKind;
  actorUserId: string | null;
  actorDisplayName: string | null;
  /**
   * Kind-specific detail as a JSON string. Callers parse this on demand
   * — see ActivityFeed's `describe` helper for the per-kind rendering.
   */
  payloadJson: string | null;
  createdAtUtc: string;
};

type AuditActivityList = {
  auditId: string;
  items: AuditActivityItem[];
};

/**
 * Query the audit's activity feed (newest first, capped at 50). Live
 * invalidation piggybacks on the existing report SignalR channel — see
 * useAuditReportRealtime — so no separate subscription is needed here.
 */
export function useAuditActivity(tenantId: string, auditId: string | undefined) {
  return useQuery({
    queryKey: auditId
      ? auditKeys.activity(tenantId, auditId)
      : ['audits', tenantId, 'activity-pending'],
    queryFn: async () => {
      const list = await api<AuditActivityList>(
        `/api/audit/audits/${auditId}/activity`);
      return list.items;
    },
    enabled: !!auditId,
    staleTime: 15_000,
  });
}

// -----------------------------------------------------------------------
//  Slice 13 — client reports (/api/client/*)
// -----------------------------------------------------------------------

const clientKeys = {
  reports:     ['client', 'reports']     as const,
  report:      (snapshotId: string) => ['client', 'reports', snapshotId] as const,
  retractions: ['client', 'retractions'] as const,
};

export type ClientReport = {
  snapshotId: string;
  auditId: string;
  auditTitle: string;
  organisationName: string;
  label: string | null;
  publishedAtUtc: string;
  publishedByDisplayName: string | null;
};

export type ClientReportSection = {
  id: string;
  key: string;
  title: string;
  displayOrder: number;
  kind: 'Narrative' | 'Computed';
  computedKind: string | null;
  revision: number | null;
  contentJson: string | null;
};

/** One attachment on a published report, as a client sees it. */
export type ClientEvidence = {
  id: string;
  sectionId: string;
  sectionCode: string;
  criterionCode: string;
  itemText: string;
  referenceCode: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  description: string | null;
  itemComment: string | null;
  uploadedAtUtc: string;
};

export type ClientReportContent = {
  snapshotId: string;
  auditId: string;
  auditTitle: string;
  organisationName: string;
  label: string | null;
  publishedAtUtc: string;
  sections: ClientReportSection[];
  /** The evidence set frozen when this version was captured. */
  evidence: ClientEvidence[];
};

/** Client download URL for one attachment on a published report. */
export function clientEvidenceDownloadUrl(
  snapshotId: string, evidenceId: string): string {
  return `/api/client/reports/${snapshotId}/evidence/${evidenceId}/content`;
}

export type ClientRetraction = {
  snapshotId: string;
  auditId: string;
  auditTitle: string;
  label: string | null;
  unpublishedAtUtc: string;
  unpublishedByDisplayName: string | null;
};

type ClientReportsList = { items: ClientReport[] };
type ClientRetractionsList = { items: ClientRetraction[] };

/** List published reports visible to the current client user. */
export function useClientReports() {
  return useQuery({
    queryKey: clientKeys.reports,
    queryFn: async () => {
      const list = await api<ClientReportsList>('/api/client/reports');
      return list.items;
    },
    staleTime: 15_000,
  });
}

/**
 * Load the full content of a single published report for the client
 * viewer. Returns undefined while loading; the error state maps to a
 * 404 (report unpublished or not owned).
 */
export function useClientReportContent(snapshotId: string | undefined) {
  return useQuery({
    queryKey: snapshotId ? clientKeys.report(snapshotId) : ['client', 'report-pending'],
    queryFn: () => api<ClientReportContent>(`/api/client/reports/${snapshotId}`),
    enabled: !!snapshotId,
    staleTime: 60_000,
  });
}

/** Recent retractions on the client's organisation (drives the banner). */
export function useClientRetractions() {
  return useQuery({
    queryKey: clientKeys.retractions,
    queryFn: async () => {
      const list = await api<ClientRetractionsList>('/api/client/retractions');
      return list.items;
    },
    staleTime: 30_000,
  });
}

// -----------------------------------------------------------------------
//  Slice 14 — staff Organisations admin (/api/organisations/*)
// -----------------------------------------------------------------------

export type Organisation = {
  id: string;
  name: string;
  createdAtUtc: string;
  userCount: number;
  auditCount: number;
};

export type ClientUser = {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string;
  isDisabled: boolean;
};

export type CreateClientUserResult = {
  user: ClientUser;
  temporaryPassword: string;
};

type OrganisationList = { items: Organisation[] };
type ClientUsersList = { items: ClientUser[] };

const orgKeys = {
  list: ['organisations'] as const,
  detail: (id: string) => ['organisations', id] as const,
  users:  (id: string) => ['organisations', id, 'users'] as const,
};

/** List every client organisation in the caller's tenant. */
export function useOrganisations() {
  return useQuery({
    queryKey: orgKeys.list,
    queryFn: async () => {
      const list = await api<OrganisationList>('/api/organisations');
      return list.items;
    },
    staleTime: 15_000,
  });
}

export function useOrganisation(id: string | undefined) {
  return useQuery({
    queryKey: id ? orgKeys.detail(id) : ['organisations', 'detail-pending'],
    queryFn: () => api<Organisation>(`/api/organisations/${id}`),
    enabled: !!id,
    staleTime: 15_000,
  });
}

export function useCreateOrganisation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string }) =>
      api<Organisation>('/api/organisations',
        { method: 'POST', body: { name: input.name } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgKeys.list });
    },
  });
}

export function useRenameOrganisation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string }) =>
      api<Organisation>(`/api/organisations/${id}`,
        { method: 'PATCH', body: { name: input.name } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgKeys.list });
      void qc.invalidateQueries({ queryKey: orgKeys.detail(id) });
    },
  });
}

export function useOrganisationUsers(id: string | undefined) {
  return useQuery({
    queryKey: id ? orgKeys.users(id) : ['organisations', 'users-pending'],
    queryFn: async () => {
      const list = await api<ClientUsersList>(`/api/organisations/${id}/users`);
      return list.items;
    },
    enabled: !!id,
    staleTime: 15_000,
  });
}

export function useCreateClientUser(organisationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      email: string;
      firstName?: string | null;
      lastName?: string | null;
    }) =>
      api<CreateClientUserResult>(`/api/organisations/${organisationId}/users`,
        { method: 'POST',
          body: {
            email:     input.email,
            firstName: input.firstName ?? null,
            lastName:  input.lastName  ?? null,
          } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgKeys.users(organisationId) });
      void qc.invalidateQueries({ queryKey: orgKeys.detail(organisationId) });
      void qc.invalidateQueries({ queryKey: orgKeys.list });
    },
  });
}

export function useDisableClientUser(organisationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api<void>(`/api/organisations/${organisationId}/users/${userId}`,
        { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgKeys.users(organisationId) });
      void qc.invalidateQueries({ queryKey: orgKeys.detail(organisationId) });
      void qc.invalidateQueries({ queryKey: orgKeys.list });
    },
  });
}

/**
 * Slice 15 — admin regenerates a client user's temp password and re-flags
 * the account so they're forced through /change-password on next login.
 * Returns the fresh temp password in the same shape as create, so the SPA
 * can reuse the one-shot temp-password panel.
 */
export function useResetClientUserPassword(organisationId: string) {
  return useMutation({
    mutationFn: (userId: string) =>
      api<CreateClientUserResult>(
        `/api/organisations/${organisationId}/users/${userId}/reset-password`,
        { method: 'POST' }),
  });
}
